import React, { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase/firebase";
import ApiService from "../services/ApiService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  // Sign up with email and password
  const signUp = async (
    email,
    password,
    displayName,
    carNumber,
    carModel,
    mobileNumber
  ) => {
    try {
      // Create Firebase user first
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update Firebase profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Register user in backend database
      const userData = {
        firebaseUid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: displayName || "",
        carNumber: carNumber || "",
        carModel: carModel || "",
        mobileNumber: mobileNumber || "",
        authProvider: "email",
      };

      const backendUser = await ApiService.registerUser(userData);
      setUserProfile(backendUser.user || backendUser);

      return userCredential;
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      ); // Fetch user profile from backend
      try {
        const profile = await ApiService.getUserProfile(
          userCredential.user.uid
        );
        console.log("Sign in - received profile:", profile);
        setUserProfile(profile.data || profile.user || profile);
      } catch (profileError) {
        console.warn("Could not fetch user profile:", profileError);
      }

      return userCredential;
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider); // Check if user exists in backend, if not register them
      try {
        const profile = await ApiService.getUserProfile(
          userCredential.user.uid
        );
        console.log("Google sign in - received profile:", profile);
        setUserProfile(profile.data || profile.user || profile);
      } catch (error) {
        // User doesn't exist in backend, register them
        const userData = {
          firebaseUid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName || "",
          carNumber: "",
          carModel: "",
          mobileNumber: "",
          authProvider: "google",
        };
        try {
          const backendUser = await ApiService.registerUser(userData);
          console.log("Google registration - received response:", backendUser);
          setUserProfile(backendUser.data || backendUser.user || backendUser);
        } catch (registerError) {
          console.warn(
            "Could not register Google user in backend:",
            registerError
          );
        }
      }

      return userCredential;
    } catch (error) {
      console.error("Google sign in error:", error);
      throw error;
    }
  };
  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };
  // Update user profile
  const updateUserProfile = async (updatedData) => {
    try {
      if (user && user.uid) {
        const updatedProfile = await ApiService.updateUserProfile(
          user.uid,
          updatedData
        );
        console.log("Update profile - received response:", updatedProfile);
        setUserProfile(
          updatedProfile.data || updatedProfile.user || updatedProfile
        );
        return updatedProfile;
      }
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Fetch user profile from backend when Firebase user changes
        try {
          const profile = await ApiService.getUserProfile(firebaseUser.uid);
          console.log("Auth state changed - received profile:", profile);
          setUserProfile(profile.data || profile.user || profile);
        } catch (error) {
          console.warn("Could not fetch user profile on auth change:", error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);
  const value = {
    user,
    userProfile,
    currentUser: user, // Alias for consistency
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
    updateUserProfile,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
